import { Card, Layout, Button, Divider } from "antd";
import {
  ProForm,
  PageContainer,
  FooterToolbar,
} from "@ant-design/pro-components";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  CustomerInformation,
  PaymentFormat,
  PartitionsInformation,
  PaymentMethod,
  PriceInfo,
  ProductsInformation,
} from "./components";
import { useEffect, useState } from "react";
import Loading from "../../components/Loading/Loading";
import { fetchClient, updateClient } from "../Clients/utils/fetch";
import {
  fetchLead,
  fetchSpecificSpecification,
  getBodyToUpdateClient,
  getBodyToUpdateLead,
  getBodyToUpdateSpecification,
  updateLead,
  updateSpecification,
} from "./utils/fetch";
import { getInitialValues } from "./utils/values";
import { getTotalPriceWithDiscount } from "./utils/price";
import { useForm } from "antd/es/form/Form";
import {
  requestSendAgreement,
  requestSendCommercialProposal,
  sendInvoiceForPayment,
  requestSendForProduction,
} from "../ClientInfo/utils/fetch";
import { banks } from "./utils/banks";
import { changePageTitle } from "../../utils/title";

const EditLead = ({ setToken }) => {
  const { clientId, leadId, applicationId } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  // true, если URL содержит ?preview=true
  const isPreview = new URLSearchParams(search).get("preview") === "true";

  const [form] = useForm();

  const [client, setClient] = useState({});
  const [isLoadingClient, setIsLoadingClient] = useState(true);
  const [lead, setLead] = useState({});
  const [isLoadingLead, setIsLoadingLead] = useState(true);
  const [clientType, setClientType] = useState("INDIVIDUAL");
  const [specification, setSpecification] = useState({});
  const [isLoadingSpecification, setIsLoadingSpecification] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [increasePrice, setIncreasePrice] = useState(0);

  // Меняем заголовок в зависимости от режима
  useEffect(() => {
    changePageTitle(isPreview ? "Предпросмотр заявки" : "Редактировать заявку");
  }, [isPreview]);

  useEffect(() => {
    fetchClient(clientId, setClient, setIsLoadingClient, () => {
      navigate("/lead");
    });
    fetchLead(leadId, setLead, setIsLoadingLead, setToken);
    fetchSpecificSpecification(
      applicationId,
      setSpecification,
      setIsLoadingSpecification
    );
  }, [clientId, leadId, applicationId]);

  useEffect(() => {
    setClientType(client?.clientType || "INDIVIDUAL");
  }, [client]);

  useEffect(() => {
    // Фильтруем заказы, удаляя пустые/null аксессуары при загрузке
    const orders = specification.orders || [];

    // Функция для проверки валидности аксессуара
    const isValidAccessory = (acc) => {
      if (!acc || acc === null || acc === undefined) return false;
      if (typeof acc === 'object' && Object.keys(acc).length === 0) return false;
      // Аксессуар должен иметь хотя бы _id или name
      if (!acc._id && !acc.name) return false;
      return true;
    };

    const cleanedOrders = orders.map((product) => {
      const cleanedProduct = { ...product };

      // Удаляем аксессуары, которые невалидны
      if (!isValidAccessory(cleanedProduct.doorKnob)) {
        delete cleanedProduct.doorKnob;
      }
      if (!isValidAccessory(cleanedProduct.doorPanel)) {
        delete cleanedProduct.doorPanel;
      }
      if (!isValidAccessory(cleanedProduct.doorstep)) {
        delete cleanedProduct.doorstep;
      }
      if (!isValidAccessory(cleanedProduct.latch)) {
        delete cleanedProduct.latch;
      }

      return cleanedProduct;
    });

    setSelectedProducts(cleanedOrders);
    if (specification?.discount) setDiscount(specification.discount);
    if (specification?.contractPrice?.percentage)
      setIncreasePrice(specification.contractPrice.percentage);
  }, [specification]);

  // Убрали эффект, который перезаписывал спецификацию данными из лида (lead.specifications[0]).
  // Теперь спецификация загружается только через fetchSpecificSpecification по конкретному applicationId,
  // что предотвращает показ данных первой спецификации вместо текущей.

  useEffect(() => {
    calculateRemains();
  }, [specification, selectedProducts]);

  const calculatePrepayment = () => {
    const totalPrice = getTotalPriceWithDiscount(
      specification.orders || [],
      form.getFieldValue("constractPriceIncreasePercentage"),
      form.getFieldValue("discount")
    );
    const prepayment = parseInt(totalPrice * 0.7, 10);
    form.setFieldValue("prepayment", prepayment);
    calculateRemains();
  };

  const calculateRemains = () => {
    const disc = form.getFieldValue("discount") || 0;
    const totalPrice = getTotalPriceWithDiscount(
      specification.orders || [],
      form.getFieldValue("constractPriceIncreasePercentage"),
      disc
    );
    form.setFieldValue(
      "remains",
      parseInt(
        totalPrice -
        (totalPrice * disc) / 100 -
        form.getFieldValue("prepayment"),
        10
      )
    );
  };

  const onValuesChange = (changed) => {
    if (changed.clientType) {
      setClientType(changed.clientType);
      if (changed.clientType === "ENTITY") {
        form.setFieldValue("companyEntity", "INTERCORP_HALYK");
      } else if (changed.clientType === "ENTITY_NO_VAT") {
        form.setFieldValue("companyEntity", "IP_KEPLER_BCC");
      } else {
        form.setFieldValue("companyEntity", "IP_KEPLER_HALYK");
      }
    }
    if (changed.discount || changed.discount === 0) setDiscount(changed.discount);
    if (
      changed.constractPriceIncreasePercentage ||
      changed.constractPriceIncreasePercentage === 0
    ) {
      setIncreasePrice(changed.constractPriceIncreasePercentage);
    }
    if (changed.accountNumber) {
      const acc = changed.accountNumber;
      if (acc.length >= 7) {
        const code = acc.substring(4, 7);
        const bank = banks.find((b) => b.code === code);
        form.setFieldValue("bankName", bank?.name || "");
        form.setFieldValue("BIC", bank?.BIC || "");
      } else {
        form.setFieldValue("bankName", "");
        form.setFieldValue("BIC", "");
      }
    }
    if (changed.prepayment) calculateRemains();
    if (
      changed.discount ||
      changed.discount === 0 ||
      changed.constractPriceIncreasePercentage ||
      changed.constractPriceIncreasePercentage === 0
    ) {
      calculatePrepayment();
    }
  };

  const handleUpdate = (values) => {
    let companyEntity = form.getFieldValue("companyEntity");
    if (clientType === "ENTITY") {
      companyEntity = "INTERCORP_HALYK";
    }
    const mergedValues = { ...values, companyEntity };

    const clientUpdateBody = getBodyToUpdateClient(mergedValues);
    delete clientUpdateBody.companyEntity; // Не обновляем глобально
    updateClient(clientId, clientUpdateBody);
    
    updateLead(leadId, getBodyToUpdateLead(mergedValues));
    updateSpecification(
      applicationId,
      getBodyToUpdateSpecification(
        {
          ...values,
          contractPriceId: specification.contractPriceId,
          paymentFormatId: specification.paymentFormatId,
        },
        selectedProducts
      ),
      () => {
        // Перезагружаем спецификацию после успешного сохранения
        fetchSpecificSpecification(
          applicationId,
          setSpecification,
          setIsLoadingSpecification
        );
      }
    );
  };

  const PARTITION_TYPE_NAME = "Алюминиевые перегородки";

  const isPartitionRow = (p) => {
    if (!p) return false;
    const typeName =
      p?.product?.productType?.name ||
      p?.productType?.name;
    if (typeName === PARTITION_TYPE_NAME) return true;
    const cfg = p?.partitionConfig;
    if (cfg && (cfg.openingHeight || cfg.openingWidth || cfg.sectionsCount)) {
      return true;
    }
    return false;
  };

  const hasPartitions = selectedProducts.some(isPartitionRow);

  if (isLoadingClient || isLoadingLead || isLoadingSpecification) {
    return <Loading />;
  }

  return (
    <Layout>
      <PageContainer
        title={isPreview ? "Предпросмотр заявки" : "Редактировать заявку"}
      >
        <Card>
          <ProForm
            form={form}
            // если превью — нет футера с кнопками
            submitter={
              isPreview
                ? false
                : {
                  render: () => (
                    <FooterToolbar>
                      <Button type="primary" htmlType="submit">
                        Обновить
                      </Button>
                      <Button
                        style={{ width: 100 }}
                        onClick={() => requestSendAgreement(leadId)}
                      >
                        Договор
                      </Button>
                      <Button
                        style={{ width: 100 }}
                        onClick={() =>
                          requestSendCommercialProposal(applicationId)
                        }
                      >
                        КП
                      </Button>
                      <Button
                        style={{ width: 150 }}
                        onClick={() => sendInvoiceForPayment(applicationId)}
                      >
                        Счет на оплату
                      </Button>
                      <Button
                        style={{ width: 200 }}
                        onClick={() => requestSendForProduction(applicationId)}
                      >
                        Заявка на производство
                      </Button>
                    </FooterToolbar>
                  ),
                }
            }
            onFinish={handleUpdate}
            onValuesChange={onValuesChange}
            initialValues={getInitialValues(
              client,
              0,
              0,
              lead,
              specification
            )}
          >
            <CustomerInformation
              clientType={clientType}
              disabled={isPreview}
            />
            <Divider />
            {hasPartitions ? (
              <PartitionsInformation
                setToken={setToken}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                clientType={clientType}
              />
            ) : (
              <ProductsInformation
                setToken={setToken}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                disabled={isPreview}
                increasePrice={form.getFieldValue(
                  "constractPriceIncreasePercentage"
                )}
                isEditMode={!isPreview}
              />
            )}
            <PriceInfo
              selectedProducts={selectedProducts}
              discount={discount}
              increasePrice={increasePrice}
              clientType={clientType}
            />
            <Divider style={{ marginTop: 0 }} />
            <div style={{ display: "flex" }}>
              <div style={{ marginRight: 40 }}>
                <PaymentFormat />
              </div>
              <div style={{ marginRight: 40 }}>
                <PaymentMethod />
              </div>
            </div>
            <Divider />
          </ProForm>

          {isPreview && (
            <FooterToolbar>
              <Button onClick={() => navigate(-1)}>Назад</Button>
            </FooterToolbar>
          )}
        </Card>
      </PageContainer>
    </Layout>
  );
};

export default EditLead;
